import { useRef, useState } from 'react'
import { Box, useTheme } from '@mui/material'
import { useAxios } from '../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import { useApp } from '../hooks/useApp'
import { RadioSelector } from '../components/atoms/RadioSelector'
import { PriceData, ScaleLabeling, ChartType } from '../types/charts'
import { MOBILE_SIZE } from '../types/constants'
import { range } from '../utils/time'
import { PriceHistoryChart } from './charts/PriceHistoryChart'
import { DateTime } from 'luxon'
import { VAT } from '../utils/vat'
import ToggleButton from '@mui/material/ToggleButton'
import LineAxisIcon from '@mui/icons-material/LineAxis'
import BarChartIcon from '@mui/icons-material/BarChart'

export interface PriceHistoryProps {
}

export function PriceHistory(props: PriceHistoryProps) {

  const app = useApp()
  const theme = useTheme()
  const { t } = useTranslation()

  const [held, setHeld] = useState<{ url: string, prices: PriceData[] } | null>(null)
  const [scale, setScale] = useState<ScaleLabeling>('quarter')
  const [minDate, maxDate, fetchDate] = range(scale)
  const country = app.user?.country?.iso_3166 || 'FI'
  let maxDatePlusOne = DateTime.fromISO(maxDate).plus({ seconds: 1 }).toUTC().toISO()
  const [chartType, setChartType] = useState<ChartType>('bar')

  if (scale === 'hour' || scale === 'quarter') {
    maxDatePlusOne = DateTime.fromISO(maxDate).plus({ hours: 24 * 2 }).plus({ seconds: 1 }).toUTC().toISO()
  }

  const url = `/data/prices?from=${fetchDate}&to=${maxDatePlusOne}&area=${country}`

  // The query most recently issued; responses for any other query are stale.
  const urlRef = useRef(url)
  urlRef.current = url

  useAxios<PriceData[]>({
    url,
    onSuccess: (data) => {
      // A response for a query the user has already switched away from must
      // not replace the current query's prices.
      if (url !== urlRef.current) {
        return
      }
      setHeld({ url, prices: data })
    }
  })

  // Only prices fetched by the query the current scale issues may be charted.
  // Coarse scales fetch years of quarter-hour price slots (fetch range is 10x
  // the visible range), and bucketing those with a fine scale's steps right
  // after a scale switch builds hundreds of thousands of chart slots, which
  // freezes the page and runs Safari out of memory. Chart nothing until the
  // held prices and the scale agree.
  const prices = held !== null && held.url === url ? held.prices : []

  const handleToggleChartType = () => {
    setChartType(prev => {
      if (prev === 'bar') {
        return 'line'
      }
      return 'bar'
    })
  }

  return <Box>
    <Box sx={{ textAlign: 'center', mb: 2, position: 'relative', display: 'flex', justifyContent: app.mobileWidth < MOBILE_SIZE ? 'space-between' : 'center' }}>
      <RadioSelector
        variant="primary"
        value={scale}
        onClick={(value) => setScale(value as ScaleLabeling)}
        values={{
          quarter: t('Scale-Quarter'),
          hour: t('Scale-Hour'),
          day: t('Scale-Day'),
          week: t('Scale-Week'),
          month: t('Scale-Month'),
          year: t('Scale-Year'),
        }}
      />
      {scale !== 'minute' && <ToggleButton
        sx={{ padding: 0.4, position: 'absolute', right: 0 }}
        value="check"
        onChange={handleToggleChartType}
      >
        {chartType === 'bar' && <LineAxisIcon sx={{ color: theme.palette.primary.main }} />}
        {chartType === 'line' && <BarChartIcon sx={{ color: theme.palette.primary.main }} />}
      </ToggleButton>}
    </Box>

    <PriceHistoryChart
      id="price-history"
      prices={prices}
      currency={app.user?.currency || 'EUR'}
      options={{
        type: chartType,
        from: minDate,
        to: maxDate,
        smoothening: 'mean',
        vat: VAT.for(country, maxDate), // TODO: This handling should be moved to chart so that we get correct VAT every day.
        key: 'price',
        timeKey: 'from',
        steps: scale,
        timestamps: false,
        compact: true,
        panX: true,
        includeLast: true,
      }}
    />
  </Box>
}
